import { clamp, hash01, lerp, smoothstep, valueNoise } from '@/core/rng';
import type { Region } from './region';

/**
 * RIVER MORPHOLOGY — the one geometry truth of a river (2026-08-19, Griffin's
 * river morphology brief).
 *
 * The hydrology is the Region's and is not touched here: `Region.down` routes
 * every drop, `Region.flow` accumulates it, and the visible network is the
 * cells above `VISIBLE_FLOW`, walked as chains from each spring down to the
 * sea or to a confluence, exactly as `render/rivers.ts` has always walked
 * them. What this module adds is everything a channel IS besides a centreline
 * and a width, derived deterministically from that network and the Region's
 * own coarse height field:
 *
 *   flow → channel form → bank → shallow → bed → thalweg → water surface
 *
 *  · **width** from discharge (the old formula), then modulated by the reach's
 *    POOL/RIFFLE phase, its confluences and nothing else — no per-cell wobble;
 *  · **depth**, a first-class property with its own range: a headwater is
 *    knee-deep, a trunk river's pools are three or four metres, and two
 *    reaches of one width can differ in depth;
 *  · **pool / riffle**: a slow deterministic field along the chain (≈ 180 m),
 *    biased by slope (gentle → pool) and curvature (bends → pool), so a
 *    pool is wider, deeper, slower and a riffle narrower, shallower, faster;
 *  · **curvature** and the **thalweg**: on a bend the deepest line moves to
 *    the outer bank, the inner bank shoals, and where the reach is shallow
 *    and the bend tight the inner bed rises above the water as a gravel bar;
 *  · **confluences**: where a chain ends in another, the receiving channel
 *    widens and deepens over ±120 m and settles;
 *  · **the water surface**: the coarse height along the centreline, smoothed
 *    over ~100 m and forced to descend (a running minimum from the spring),
 *    set a bank's height below the land — water finds a level through a
 *    carved channel, it does not follow every hummock; and the **bed** is
 *    that surface minus the local depth;
 *  · **speed**, a presentation proxy from slope, depth and phase.
 *
 * ONE MODEL, MANY READERS. `surfaceHeight` carves the terrain with `carve()`,
 * the water renderer lays its surface from the same stations, the ground's
 * material and the plant placement read `at()` — so the terrain and the water
 * describe the same river, and nothing that stands on `surfaceHeight` floats.
 * Every number is a pure function of the Region, so the main thread and the
 * chunk workers build the identical network (law 1; law 7 untouched — nothing
 * here is read by the simulation).
 *
 * Cost: the network is built once per Region (~36k cells walked, the chains
 * resampled to 12 m stations) and cached; a query is a bucket lookup and a
 * handful of segment projections, and returns null fast everywhere else —
 * `surfaceHeight` pays ~100 ns for it away from water.
 */

/**
 * Accumulated flow above which a cell carries visible surface water. 620 →
 * 250 on 2026-08-19, when the half-cell index bug in `render/rivers.ts` was
 * found (see that file's note): at 620 the island has two rivers; at 250,
 * fifteen with tributaries, which is where `chunk.ts`'s channel damping
 * already begins. Lives here now, so the terrain, the water and the plants
 * agree on what a river is.
 */
export const VISIBLE_FLOW = 250;

/** Spacing of the stations a chain is resampled to, metres. */
const STATION = 12;
/** Bucket size of the spatial index, metres — wider than any channel's reach. */
const BUCKET = 96;
/** Furthest a query looks from a centreline, metres (bank included). */
const REACH = 56;
/** The pool/riffle field's wavelength along the chain, metres. */
const POOL_WAVE = 180;
/** The coarse height is smoothed over this many stations either side for the water surface. */
const SMOOTH = 5;

const S_POOL = 0x5c71, S_BANK = 0x5d72;

/** The reading at one point near a river. */
export interface RiverSample {
  /** Signed lateral distance from the centreline, metres; + is the LEFT bank looking downstream. */
  d: number;
  /** Metres downstream from the chain's spring. */
  along: number;
  /** Wetted half-width — the water's edge is at |d| = halfWidth. */
  halfWidth: number;
  /** Bank-to-bank half-width — the carve begins here. */
  channelHalf: number;
  /** The reach's greatest depth (at the thalweg), metres. */
  depth: number;
  /** Water depth beneath the surface AT this point, 0 outside the water. */
  localDepth: number;
  /** The water surface's height here. */
  waterY: number;
  /** The bed's (carved terrain's) height at this point — above `waterY` on a bar. */
  bedY: number;
  /** Downstream unit tangent. */
  tx: number; tz: number;
  /** Signed curvature, 1/m; + bends left. */
  curvature: number;
  /** A presentation proxy, 0..1: how fast the water looks. */
  speed: number;
  /** −1 riffle … +1 pool. */
  pool: number;
  /** Lateral offset of the deepest line, metres, signed like `d`. */
  thalweg: number;
  /** 0..1: how much a confluence is widening and deepening this reach. */
  confluence: number;
  /** How steep the water runs here, metres per metre, ≥ 0. */
  slope: number;
  /** Which chain and station, for anything that wants to name "this reach". */
  chain: number; station: number;
  /** Inside the water (|d| < halfWidth and the bed below the surface). */
  inWater: boolean;
  /** Inside the channel at all (|d| < channelHalf). */
  inChannel: boolean;
}

/** One station of a resampled chain: everything the cross-section needs. */
export interface RiverStation {
  x: number; z: number;
  along: number;
  flow: number;
  tx: number; tz: number;
  curvature: number;
  slope: number;
  pool: number;
  confluence: number;
  halfWidth: number;
  channelHalf: number;
  depth: number;
  waterY: number;
  thalweg: number;
  speed: number;
}

export interface RiverChain {
  id: number;
  stations: RiverStation[];
  /** Index of the chain this one flows into, or −1 for the sea. */
  into: number;
}

export interface RiverNetwork {
  readonly chains: readonly RiverChain[];
  /** The reading at a point, or null if no river is within reach. */
  at(x: number, z: number): RiverSample | null;
  /**
   * The terrain height at (x, z) with the channel carved into it. `h` is the
   * uncarved drawn surface; `smooth` the same without its fine noise — the
   * bank is cut against that and the noise returns over the outer bank.
   */
  carve(x: number, z: number, h: number, smooth: number): number;
}

/** Wetted half-width from discharge — the old `render/rivers.ts` law, kept. */
export const baseHalfWidth = (flow: number): number =>
  clamp(Math.log(flow / VISIBLE_FLOW + 1) * 9, 5, 34) / 2;
/** Thalweg depth from discharge: knee-deep at the threshold, 2.8 m at 20,000 of flow, before pools. */
export const baseDepth = (flow: number): number =>
  0.35 + 0.55 * Math.log(flow / VISIBLE_FLOW + 1);
/** How far the water sits below the land at the bank shoulder, from discharge. */
const bankDrop = (flow: number): number =>
  0.35 + 0.18 * Math.log(flow / VISIBLE_FLOW + 1);

/** Smooth 1-D value noise along a chain, in [−1, 1]. */
function along1(s: number, salt: number): number {
  return (valueNoise(s, 0.37, salt) * 0.7 + valueNoise(s * 2.3 + 11.1, 0.61, salt + 1) * 0.3 - 0.5) * 2;
}

const cache = new WeakMap<Region, RiverNetwork>();

/** The network for a Region — built once, cached for the Region's life. */
export function riversOf(region: Region): RiverNetwork {
  let net = cache.get(region);
  if (!net) { net = buildNetwork(region); cache.set(region, net); }
  return net;
}

function buildNetwork(region: Region): RiverNetwork {
  const { n, cell } = region;
  // the Region's own cell centre — `nearest()` rounds, so this is exact
  const centre = (i: number) => (i - n / 2) * cell;

  // ---- the network: chains from every spring, along `down`, claimed once ----
  const qualifies = new Uint8Array(n * n);
  for (let z = 1; z < n - 1; z++) for (let x = 1; x < n - 1; x++) {
    const i = z * n + x;
    if (!region.ocean[i] && region.flow[i] >= VISIBLE_FLOW) qualifies[i] = 1;
  }
  const fed = new Uint8Array(n * n);
  for (let i = 0; i < n * n; i++) {
    if (qualifies[i] && region.down[i] >= 0 && qualifies[region.down[i]]) fed[region.down[i]] = 1;
  }
  const claimedBy = new Int32Array(n * n).fill(-1);
  const raw: Array<{ pts: number[]; endsIn: number; endCell: number }> = [];   // x, z, flow triplets
  for (let spring = 0; spring < n * n; spring++) {
    if (!qualifies[spring] || fed[spring]) continue;
    const pts: number[] = [];
    let i = spring, guard = 0, endsIn = -1, endCell = -1;
    while (i >= 0 && guard++ < 4096) {
      const cx = i % n, cz = (i / n) | 0;
      pts.push(centre(cx), centre(cz), Math.max(region.flow[i], VISIBLE_FLOW));
      if (!qualifies[i]) break;                       // onto the shore or a sink
      if (claimedBy[i] >= 0 && i !== spring) { endsIn = claimedBy[i]; endCell = i; break; }
      claimedBy[i] = raw.length;
      i = region.down[i];
    }
    if (pts.length < 9) continue;
    raw.push({ pts, endsIn, endCell });
  }

  // ---- smooth (two rounds of Chaikin) and resample to stations ----
  const chains: RiverChain[] = [];
  const chainSpring: number[] = [];
  for (let c = 0; c < raw.length; c++) {
    let line = raw[c].pts;
    for (let r = 0; r < 2; r++) {
      const out: number[] = [line[0], line[1], line[2]];
      for (let k = 0; k + 5 < line.length; k += 3) {
        const ax = line[k], az = line[k + 1], aw = line[k + 2];
        const bx = line[k + 3], bz = line[k + 4], bw = line[k + 5];
        out.push(ax * 0.75 + bx * 0.25, az * 0.75 + bz * 0.25, aw * 0.75 + bw * 0.25);
        out.push(ax * 0.25 + bx * 0.75, az * 0.25 + bz * 0.75, aw * 0.25 + bw * 0.75);
      }
      out.push(line[line.length - 3], line[line.length - 2], line[line.length - 1]);
      line = out;
    }
    // resample at STATION metres
    const st: RiverStation[] = [];
    const m = line.length / 3;
    let carry = 0, along = 0;
    const push = (x: number, z: number, flow: number, s: number) =>
      st.push({ x, z, along: s, flow, tx: 0, tz: 0, curvature: 0, slope: 0, pool: 0, confluence: 0,
                halfWidth: 0, channelHalf: 0, depth: 0, waterY: 0, thalweg: 0, speed: 0 });
    push(line[0], line[1], line[2], 0);
    for (let k = 0; k + 1 < m; k++) {
      const ax = line[k * 3], az = line[k * 3 + 1], af = line[k * 3 + 2];
      const bx = line[(k + 1) * 3], bz = line[(k + 1) * 3 + 1], bf = line[(k + 1) * 3 + 2];
      const L = Math.hypot(bx - ax, bz - az);
      let t = (STATION - carry) / L;
      while (t <= 1) {
        along += STATION;
        push(ax + (bx - ax) * t, az + (bz - az) * t, af + (bf - af) * t, along);
        t += STATION / L;
      }
      carry = (carry + L) % STATION;
    }
    if (st.length < 3) continue;
    chainSpring.push(c);
    chains.push({ id: chains.length, stations: st, into: -1 });
  }
  // confluence targets: raw index → chain index
  const chainOfRaw = new Int32Array(raw.length).fill(-1);
  chainSpring.forEach((r, ci) => { chainOfRaw[r] = ci; });
  chains.forEach((ch, ci) => { const e = raw[chainSpring[ci]].endsIn; ch.into = e >= 0 ? chainOfRaw[e] : -1; });

  // ---- per-station morphology ----
  // confluences: where a tributary ends, the receiving chain's nearest station
  const confluenceAt: Array<{ chain: number; station: number; weight: number }> = [];
  chains.forEach((ch, ci) => {
    if (ch.into < 0) return;
    const end = ch.stations[ch.stations.length - 1];
    const host = chains[ch.into];
    let best = -1, bd = Infinity;
    host.stations.forEach((s, k) => { const d = Math.hypot(s.x - end.x, s.z - end.z); if (d < bd) { bd = d; best = k; } });
    if (best >= 0) confluenceAt.push({ chain: ch.into, station: best, weight: clamp(Math.log(end.flow / VISIBLE_FLOW + 1) / 3, 0.3, 1) });
  });
  chains.forEach((ch, ci) => {
    const st = ch.stations;
    const N = st.length;
    // tangents and curvature from neighbours
    for (let k = 0; k < N; k++) {
      const a = st[Math.max(0, k - 1)], b = st[Math.min(N - 1, k + 1)];
      const dx = b.x - a.x, dz = b.z - a.z, dl = Math.hypot(dx, dz) || 1;
      st[k].tx = dx / dl; st[k].tz = dz / dl;
    }
    for (let k = 0; k < N; k++) {
      const a = st[Math.max(0, k - 2)], b = st[Math.min(N - 1, k + 2)];
      const ta = Math.atan2(a.tz, a.tx), tb = Math.atan2(b.tz, b.tx);
      let dth = tb - ta;
      while (dth > Math.PI) dth -= Math.PI * 2;
      while (dth < -Math.PI) dth += Math.PI * 2;
      const ds = Math.max(1, b.along - a.along);
      // + bends LEFT looking downstream (y up, x right, z toward the viewer:
      // turning toward −z… the sign only has to be consistent with `d` below)
      st[k].curvature = -dth / ds;
    }
    // the coarse height along the line, smoothed, and the slope from it
    const hc = st.map((s) => region.heightAt(s.x, s.z));
    const hs = hc.map((_, k) => {
      let sum = 0, cnt = 0;
      for (let j = -SMOOTH; j <= SMOOTH; j++) { const q = k + j; if (q >= 0 && q < N) { sum += hc[q]; cnt++; } }
      return sum / cnt;
    });
    for (let k = 0; k < N; k++) {
      const a = Math.max(0, k - 3), b = Math.min(N - 1, k + 3);
      st[k].slope = Math.max(0, (hs[a] - hs[b]) / Math.max(1, st[b].along - st[a].along));
    }
    // confluence influence on this chain
    const conf = new Float32Array(N);
    for (const c of confluenceAt) {
      if (c.chain !== ci) continue;
      for (let k = 0; k < N; k++) {
        const d = Math.abs(st[k].along - st[c.station].along);
        // a bump ±120 m, a little longer downstream than up
        const w = k >= c.station ? 1 - smoothstep(0, 160, d) : 1 - smoothstep(0, 90, d);
        conf[k] = Math.max(conf[k], w * c.weight);
      }
    }
    // the pool/riffle phase, then everything that hangs on it
    const chainSalt = S_POOL + ci * 131;
    for (let k = 0; k < N; k++) {
      const s = st[k];
      const field = along1(s.along / POOL_WAVE, chainSalt);
      const slopeN = clamp(s.slope * 30, 0, 1);                       // 0 flat … 1 steep (3%+)
      const bend = clamp(Math.abs(s.curvature) * 70, 0, 1);
      // gentle reaches and bends pool; steep reaches riffle
      s.pool = clamp(field * 0.8 + (0.5 - slopeN) * 0.6 + bend * 0.35, -1, 1);
      s.confluence = conf[k];
      const hw0 = baseHalfWidth(s.flow);
      s.halfWidth = hw0 * (1 + 0.22 * s.pool + 0.35 * s.confluence);
      // the bank: a little over the water, wider on a pool, never under a metre
      s.channelHalf = s.halfWidth * (1.40 - 0.12 * s.pool) + 1.2;
      s.depth = Math.max(0.25, baseDepth(s.flow) * (1 + 0.45 * s.pool - 0.25 * slopeN + 0.35 * s.confluence));
      // the thalweg: to the outer bank on a bend — a left bend (+κ) has its
      // outer bank on the right (−d)
      s.thalweg = -Math.sign(s.curvature) * Math.min(0.55, Math.abs(s.curvature) * 60) * s.halfWidth;
      s.speed = clamp(0.22 + slopeN * 0.6 + (1 - s.pool) * 0.18 - s.depth * 0.05, 0.08, 1);
      // the water surface: the smoothed coarse height a bank below the land
      s.waterY = hs[k] - bankDrop(s.flow);
    }
    // …and it descends LOCALLY: a trailing-window minimum (four stations,
    // ~50 m), so a hummock or a short swell of the coarse field is levelled
    // into a pool rather than followed — but a long climb is followed. The
    // island's routed flow crosses sills (the Region routed on a flood-filled
    // field and kept the raw one), so the coarse height along a chain rises
    // for kilometres in places; a global running minimum there put the water
    // 40–140 m underground (measured, `tools/rivers.ts`), and a global
    // running maximum from the mouth would float it 40 m in the air. Lakes in
    // those basins are a later pass; this one follows the land.
    const raw = st.map((s) => s.waterY);
    for (let k = 0; k < N; k++) {
      let m = raw[k];
      for (let j = 1; j <= 4; j++) if (k - j >= 0) m = Math.min(m, raw[k - j]);
      st[k].waterY = m;
    }
    for (let k = 0; k < N; k++) st[k].waterY = Math.max(st[k].waterY, 0.55);
  });
  // second pass: tributary mouths step down to their host's water over the
  // last ~100 m, so nothing pours uphill into a pool
  const fixMouths = () => {
    chains.forEach((ch) => {
      if (ch.into < 0) return;
      const host = chains[ch.into];
      const st = ch.stations, N = st.length, end = st[N - 1];
      let best: RiverStation | null = null, bd = Infinity;
      for (const h of host.stations) { const d = Math.hypot(h.x - end.x, h.z - end.z); if (d < bd) { bd = d; best = h; } }
      if (!best) return;
      const target = best.waterY;
      for (let k = 0; k < N; k++) {
        const back = end.along - st[k].along;
        if (back > 100) continue;
        const t = 1 - back / 100;
        st[k].waterY = Math.min(st[k].waterY, lerp(st[k].waterY, target, t * t));
      }
    });
  };
  // hosts first, then their tributaries, then theirs: three rounds is plenty
  fixMouths(); fixMouths(); fixMouths();

  // ---- the spatial index: stations by bucket ----
  const buckets = new Map<number, number[]>();          // key → [chain, station, chain, station, …]
  const key = (bx: number, bz: number) => (bx + 2048) * 4096 + (bz + 2048);
  chains.forEach((ch, ci) => ch.stations.forEach((s, k) => {
    const bx = Math.floor(s.x / BUCKET), bz = Math.floor(s.z / BUCKET);
    const kk = key(bx, bz);
    let list = buckets.get(kk);
    if (!list) { list = []; buckets.set(kk, list); }
    list.push(ci, k);
  }));
  // a cheap "is anything near" bitmap at bucket resolution, one ring wide
  const near = new Set<number>();
  for (const kk of buckets.keys()) {
    const bx = Math.floor(kk / 4096) - 2048, bz = (kk % 4096) - 2048;
    for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) near.add(key(bx + dx, bz + dz));
  }

  const sample: RiverSample = {
    d: 0, along: 0, halfWidth: 0, channelHalf: 0, depth: 0, localDepth: 0, waterY: 0, bedY: 0,
    tx: 0, tz: 0, curvature: 0, speed: 0, pool: 0, thalweg: 0, confluence: 0, slope: 0,
    chain: -1, station: -1, inWater: false, inChannel: false,
  };

  /**
   * The bed's height at lateral `d` for a reach's morphology. Inside the
   * water: a parabola about the thalweg, full depth there, zero at the
   * water's edge, shoaling into a gravel bar on the inside of a shallow tight
   * bend. Outside it: the bank, rising from the water's edge to the uncarved
   * land at channelHalf — steeper on a bend's outer bank, gentler inside.
   * No allocation: this runs per terrain vertex.
   */
  const bedAt = (hw: number, channelHalf: number, depth0: number, waterY: number, thalweg: number,
                 curvature: number, pool: number, d: number, hUncarved: number): number => {
    const outerSide = thalweg !== 0 && Math.sign(d) === Math.sign(thalweg);
    const ad = Math.abs(d);
    if (ad <= hw) {
      const span = outerSide ? hw - Math.abs(thalweg) : hw + Math.abs(thalweg);
      const u = Math.abs(d - thalweg) / Math.max(0.5, span);
      let depth = depth0 * Math.pow(Math.max(0, 1 - u * u), 0.8);
      const bend = clamp(Math.abs(curvature) * 70, 0, 1);
      const inner = thalweg !== 0 && !outerSide;
      if (inner && bend > 0.25) {
        const bar = bend * (1 - smoothstep(-0.2, 0.6, pool)) * smoothstep(0.35, 0.95, ad / hw);
        depth -= bar * (depth0 * 0.9 + 0.35);
      }
      return waterY - depth;
    }
    const t = clamp((ad - hw) / Math.max(0.5, channelHalf - hw), 0, 1);
    const steep = outerSide ? 0.55 : 1.0;
    const rise = Math.pow(t, 0.55 * steep + 0.55);
    return waterY + (hUncarved - waterY) * rise;
  };

  const at = (x: number, z: number): RiverSample | null => {
    const bx = Math.floor(x / BUCKET), bz = Math.floor(z / BUCKET);
    if (!near.has(key(bx, bz))) return null;
    // the nearest segment among the stations in the surrounding buckets
    let bestChain = -1, bestK = -1, bestT = 0, bestD2 = REACH * REACH, bestPx = 0, bestPz = 0;
    for (let dz = -1; dz <= 1; dz++) for (let dx = -1; dx <= 1; dx++) {
      const list = buckets.get(key(bx + dx, bz + dz));
      if (!list) continue;
      for (let i = 0; i < list.length; i += 2) {
        const ci = list[i], k = list[i + 1];
        const st = chains[ci].stations;
        const a = st[k], b = st[Math.min(st.length - 1, k + 1)];
        const ex = b.x - a.x, ez = b.z - a.z;
        const el2 = ex * ex + ez * ez;
        let t = el2 > 1e-6 ? ((x - a.x) * ex + (z - a.z) * ez) / el2 : 0;
        t = clamp(t, 0, 1);
        const px = a.x + ex * t, pz = a.z + ez * t;
        const d2 = (x - px) * (x - px) + (z - pz) * (z - pz);
        if (d2 < bestD2) { bestD2 = d2; bestChain = ci; bestK = k; bestT = t; bestPx = px; bestPz = pz; }
      }
    }
    if (bestChain < 0) return null;
    const st = chains[bestChain].stations;
    const a = st[bestK], b = st[Math.min(st.length - 1, bestK + 1)];
    const t = bestT;
    const mix = (p: number, q: number) => p + (q - p) * t;
    const tx = mix(a.tx, b.tx), tz = mix(a.tz, b.tz);
    const tl = Math.hypot(tx, tz) || 1;
    const ux = tx / tl, uz = tz / tl;
    // signed lateral: left of the flow is +
    const d = (x - bestPx) * (-uz) + (z - bestPz) * ux;
    const s = sample;
    s.d = d; s.along = mix(a.along, b.along);
    s.halfWidth = mix(a.halfWidth, b.halfWidth); s.channelHalf = mix(a.channelHalf, b.channelHalf);
    s.depth = mix(a.depth, b.depth); s.waterY = mix(a.waterY, b.waterY);
    s.tx = ux; s.tz = uz; s.curvature = mix(a.curvature, b.curvature);
    s.speed = mix(a.speed, b.speed); s.pool = mix(a.pool, b.pool); s.thalweg = mix(a.thalweg, b.thalweg);
    s.confluence = mix(a.confluence, b.confluence); s.slope = mix(a.slope, b.slope);
    s.chain = bestChain; s.station = bestK;
    s.inChannel = Math.abs(d) < s.channelHalf;
    // the bed here — inside the water it does not depend on the land; on the
    // bank `carve` asks with the land in hand, so here the bank reads as the water
    s.bedY = Math.abs(d) <= s.halfWidth
      ? bedAt(s.halfWidth, s.channelHalf, s.depth, s.waterY, s.thalweg, s.curvature, s.pool, d, s.waterY)
      : s.waterY;
    s.localDepth = Math.abs(d) <= s.halfWidth ? Math.max(0, s.waterY - s.bedY) : 0;
    s.inWater = s.localDepth > 0.02;
    return s;
  };

  const carve = (x: number, z: number, h: number, smooth: number): number => {
    const s = at(x, z);
    if (!s || !s.inChannel) return h;
    const ad = Math.abs(s.d);
    if (ad <= s.halfWidth) {
      // the bed: never higher than the land was (a hollow that was already
      // deeper stays deeper — deeper water, no artefact)
      const bed = bedAt(s.halfWidth, s.channelHalf, s.depth, s.waterY, s.thalweg, s.curvature, s.pool, s.d, h);
      return Math.min(h, bed);
    }
    // the bank: from a hand above the water at the edge up to the land at
    // channelHalf. The reference land is the SMOOTH surface near the water
    // (so the bank is cut against the valley floor, not a hummock) fading to
    // the full surface at the shoulder; a hollow beside the water that lies
    // under the water's level is raised onto a low levee, and the whole thing
    // blends back to the uncarved land over the outer sixth of the bank.
    const t = clamp((ad - s.halfWidth) / Math.max(0.5, s.channelHalf - s.halfWidth), 0, 1);
    const ref = lerp(smooth, h, smoothstep(0.55, 1.0, t));
    const bank = bedAt(s.halfWidth, s.channelHalf, s.depth, s.waterY, s.thalweg, s.curvature, s.pool, s.d,
                       Math.max(ref, s.waterY + 0.08));
    return lerp(bank, h, smoothstep(0.84, 1.0, t));
  };

  return { chains, at, carve };
}

/** Per-bank character, for the ground's material: 0 low alluvial … 1 cut bank. */
export function bankCut(s: RiverSample): number {
  const outer = s.thalweg !== 0 && Math.sign(s.d) === Math.sign(s.thalweg);
  const bend = clamp(Math.abs(s.curvature) * 70, 0, 1);
  return clamp((outer ? bend : 0) * 0.8 + hash01(Math.round(s.along / 40), s.chain, S_BANK) * 0.2, 0, 1);
}
